import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { Calendar, toDateRange } from 'src/components/ui/calendar';
import moment from 'moment';
import type { CustomDateRangeValues } from 'src/pages/AnalyticsPage/AnalyticsCustomer/AnalyticsCoCustomer.types';

export const AnalyticsDateRangePopupForm = ({
  onSubmit,
  dateDefault,
  onCanSubmitChange = () => {},
}: {
  onSubmit: (values: CustomDateRangeValues) => void;
  dateDefault: CustomDateRangeValues['date'];
  // Reports whether the picked range has both ends, so the dialog can gate its submit button.
  onCanSubmitChange?: (canSubmit: boolean) => void;
}) => {
  const [monthState, setMonthState] = useState(
    moment(dateDefault.to).subtract(1, 'months').toDate(),
  );
  const { watch, handleSubmit, control, setValue } = useForm<CustomDateRangeValues>({
    defaultValues: {
      date: {
        from: '',
        to: '',
      },
    },
    resolver: formResolver(
      object({
        date: object({
          from: stringScheme({ required: false }).label('Date from'),
          to: stringScheme({ required: false }).label('Date to'),
        }),
      }),
    ),
  });
  const date = watch('date');
  const canSubmit = !!date?.from && !!date?.to;

  const submit = (formValues: CustomDateRangeValues) => {
    onSubmit(formValues);
  };

  useEffect(() => {
    setValue('date', dateDefault);
  }, [dateDefault]);

  useEffect(() => {
    onCanSubmitChange(canSubmit);
  }, [canSubmit]);

  return (
    <form
      id="aply-date-filter-form"
      onSubmit={handleSubmit(submit)}
      className="flex flex-col gap-3 font-jamjuree"
    >
      <Controller
        control={control}
        name="date"
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <Calendar
            variant="filter"
            resetOnSelect
            mode="range"
            disabled={{ after: new Date() }}
            month={monthState}
            selected={toDateRange(value)}
            onSelect={onChange}
            numberOfMonths={2}
            onMonthChange={setMonthState}
          />
        )}
      />
      <p className="text-xs leading-normal font-medium text-grey_450">
        {date?.from ? moment(date.from).format('ll') : 'Start date'} –{' '}
        {date?.to ? moment(date.to).format('ll') : 'End date'}
      </p>
    </form>
  );
};
